import React from 'react';
import propTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { CircularProgress, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Autocomplete, MenuItem } from '../../theme/MUIImports';
import { Button } from '..';
import { actionCreateDomainandJiraTicket } from '../../actions/user';
import { useStyles } from './styles';
import { api } from '../../utils/api';
import { getTargets } from '../../utils/targetDb';

export const CreateDomainForm = ({ handleClose, handleOpenSnackMessage, navigate, location, isTestMode }) => {
    const classes = useStyles();
    const { createDomainLoader, sideMenu } = useSelector(state => state.user)
    const dispatch = useDispatch();
    // Consolidated targets from runtime config
    const targets = getTargets();

    // Generate initial values dynamically based on targets
    const generateInitialValues = () => {
        const values = {
            domain_name: '',
            ticket_name: '',
            namespace: '',
            db_target: 'default',
        };
        
        // Add flag fields for each target dynamically
        targets.forEach(target => {
            values[`use_${target.key}_db`] = false;
        });
        
        return values;
    };

    // Generate flag handling logic dynamically
    const handleTargetChange = (value, formik) => {
        formik.setFieldValue('db_target', value);
        
        // Reset all flags to false first
        targets.forEach(target => {
            formik.setFieldValue(`use_${target.key}_db`, false);
        });
        
        // Set the selected target flag to true
        if (value !== 'default') {
            formik.setFieldValue(`use_${value}_db`, true);
        }
    };

    // Namespace state for test mode
    const [namespaces, setNamespaces] = React.useState([]);

    React.useEffect(() => {
        if (isTestMode) {
            api.get('/namespace_map/list')
                .then(res => {
                    if (Array.isArray(res.data?.data?.namespaces)) {
                        setNamespaces(res.data.data.namespaces);
                    } else {
                        setNamespaces([]);
                    }
                })
                .catch(() => setNamespaces([]));
        }
    }, [isTestMode]);

    // Dynamic validation schema
    const validationSchema = isTestMode
        ? yup.object({
            domain_name: yup
                .string('Enter Domain')
                .min(3, 'Domain should be of minimum 3 characters length')
                .trim()
                .required('Domain is required'),
            namespace: yup
                .string()
                .required('Namespace is required'),
        })
        : yup.object({
            domain_name: yup
                .string('Enter Domain')
                .min(3, 'Domain should be of minimum 3 characters length')
                .trim()
                .required('Domain is required'),
            ticket_name: yup
                .string('Enter your ticket')
                .trim()
                .min(3, 'Ticket should be of minimum 3 characters length')
                .required('Ticket is required'),
        });

    const formik = useFormik({
        initialValues: generateInitialValues(),
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            let submitValues = { ...values };
            // Normalize flags based on dropdown selection - data-driven approach
            targets.forEach(target => {
                submitValues[`use_${target.key}_db`] = submitValues.db_target === target.key;
            });
            if (isTestMode) {
                submitValues.ticket_name = 'DUMMY_JIRA_TICKET';
            }
            const res = await dispatch(actionCreateDomainandJiraTicket(submitValues, sideMenu))
            if (res.status) {
                handleOpenSnackMessage({ open: true, type: "success", message: res?.message });
                handleClose();
                location?.pathname !=='/query-editor' && navigate('/query-editor')
            } else {
                handleOpenSnackMessage({ open: true, type: "error", message: res?.message })
            }
        },
    });

    return (
        <div className={classes['btn-primary-outlined']}>
            <DialogTitle className={classes.modalHeader} id="alert-dialog-title">ENTER DETAILS TO RUN A NEW QUERY</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <>
                    <DialogContent  >
                        {/* Namespace dropdown for test mode */}
                        {isTestMode && (
                            <Autocomplete
                                size="small"
                                options={namespaces}
                                value={formik.values.namespace || ''}
                                onChange={(_, value) => formik.setFieldValue('namespace', value || '')}
                                renderInput={(params) => (
                                    <TextField {...params} label="Namespace" variant="outlined" sx={{ my: 1, minWidth: 180 }}
                                        error={formik.touched.namespace && Boolean(formik.errors.namespace)}
                                        helperText={formik.touched.namespace && formik.errors.namespace}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option === value}
                                getOptionLabel={(option) => option || ''}
                                clearOnEscape
                                fullWidth
                            />
                        )}
                        <DialogContentText id="alert-dialog-URL" component={'span'}>
                            <TextField sx={{ my: 1 }}
                                value={formik.values.domain_name}
                                onChange={formik.handleChange} name="domain_name"
                                error={formik.touched.domain_name && Boolean(formik.errors.domain_name)}
                                helperText={formik.errors.domain_name && formik.errors.domain_name}
                                fullWidth id="outlined-domain_name" size='small' label="Domain URL" variant="outlined" />
                        </DialogContentText>
                        {/* ##### munir commented */}
                        {!isTestMode && (
                            <DialogContentText component={'span'} id="alert-dialog-Jira Ticket / Jira Link" >
                                <TextField sx={{ my: 2, width: '50%' }}
                                    value={formik.values.ticket_name}
                                    onChange={formik.handleChange}
                                    name="ticket_name"
                                    error={formik.touched.ticket_name && Boolean(formik.errors.ticket_name)}
                                    helperText={formik.errors.ticket_name && formik.errors.ticket_name}
                                    id="outlined-Jira Link" label="JIRA Ticket /JIRA Link" size='small' variant="outlined" />
                            </DialogContentText>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {!!sideMenu?.length && <Button variant="btn-secondary-small" disabled={createDomainLoader} text="Cancel" onClick={handleClose} />}
                        {/* Database Target selection */}
                        <div style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', minWidth: 240 }}>
                            <TextField
                                id="db_target"
                                select
                                size="small"
                                label="Use Target DB"
                                value={formik.values.db_target}
                                onChange={(e) => handleTargetChange(e.target.value, formik)}
                                sx={{ minWidth: 240 }}
                            >
                                <MenuItem value="default">None</MenuItem>
                                {targets.map(t => (
                                    <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                                ))}
                            </TextField>
                        </div>
                        <Button variant="btn-primary-small"
                            type="submit"
                            disabled={createDomainLoader || (isTestMode && !formik.values.namespace)}
                            text={createDomainLoader ? <CircularProgress color="inherit" size={10} /> : "Create"}
                            autoFocus />
                    </DialogActions>
                </>
            </form>
        </div>
    )
}

CreateDomainForm.propTypes = {
    handleClose: propTypes.func,
    handleOpenSnackMessage: propTypes.func
}